// ** React Imports
import { useEffect } from 'react'

// ** Third Party Imports
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
	createSearchParams,
	useNavigate,
	useSearchParams
} from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { printError } from '@/shared/api'
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks'

// ** Action Imports
import { loginThunk } from '../model/loginThunk'

// ** Type Imports
import { type UserDTO, UserRoleCode } from '@/entities/user'
import { EMAIL, addDotAtEnd } from '@/shared/lib'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useInitXeroAuthMutation } from '../model/xeroLogin'
import { xeroLoginThunk } from '../model/xeroLoginThunk'

const schema = yup.object().shape({
	email: yup
		.string()
		.matches(EMAIL, 'Email must be a valid email.')
		.required('Email is a required field.'),
	password: yup.string().required('Password is a required field.'),
	terms: yup.boolean()
})

const defaultValues = {
	email: '',
	password: '',
	terms: false
}

export const useLoginCard = () => {
	// ** State

	// ** Hooks
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const { error, isLoading } = useAppSelector((state) => state.login)
	const [searchParams] = useSearchParams()
	const { executeRecaptcha } = useGoogleReCaptcha()

	useEffect(() => {
		if (
			error !== null &&
			error === "You don't have permission to access this page"
		) {
			toast.error(addDotAtEnd(printError(error)))
		}
	}, [error])

	// React hook form
	const {
		control,
		handleSubmit,
		trigger,
		formState: { errors }
	} = useForm({
		defaultValues,
		mode: 'onChange',
		resolver: yupResolver(schema)
	})

	const onSubmit = async (formData: any) => {
		if (!executeRecaptcha) return
		const token = await executeRecaptcha('login')

		dispatch(
			loginThunk({
				email: formData.email,
				password: formData.password,
				keepMeLoggedIn: formData.terms,
				token
			})
		)
			.unwrap()
			.then((data: UserDTO) => {
				// If the user has not completed account activation, redirect to registration
				if (!data.isAccountActivated) {
					navigate({
						pathname: '/register',
						search: createSearchParams({
							step: '2',
							userId: data.id.toString()
						}).toString()
					})
				} else {
					// Redirect based on user role
					if (data.role.code === UserRoleCode.SUPERADMIN) {
						navigate('/admin/dashboard')
					} else {
						navigate('/client-dashboard')
					}
				}

				// if (
				// 	!data.organization.isFormSubmitted &&
				// 	data.organization.isFormSkiped
				// ) {
				// 	navigate('/schedule')
				// }
			})
	}

	// ** Xero Callback Params
	const code = searchParams.get('code')
	const state = searchParams.get('state')

	// ** Effects
	useEffect(() => {
		if (code && state) {
			dispatch(xeroLoginThunk({ code, state }))
		}
	}, [code, state, dispatch])

	const [initXeroAuth] = useInitXeroAuthMutation()

	const handleLoginWithXero = async () => {
		try {
			const res = await initXeroAuth({}).unwrap()
			if (res?.authorizationUrl) {
				window.location.href = res.authorizationUrl
			}
		} catch (error) {
			console.error('Failed to initialize Xero login', error)
		}
	}

	return {
		// form
		errors,
		control,
		onSubmit,
		handleSubmit,
		// request
		isLoading,
		trigger,
		handleLoginWithXero,
		code,
		state
	}
}
